// The spike's saves (gate A4.1), on @rpg/save: three slots and an autosave in IndexedDB, documents through the
// spike's schema (./schema.ts: the sim slice, its versions and migration, A1's bare saves lifted by the legacy hook),
// compressed, and synced. The remote is the player's account on the shared Supabase project (decision 0033: the `rpg`
// schema, the cozy game's auth) when the build has the project's public URL and key (`VITE_SUPABASE_URL`,
// `VITE_SUPABASE_PUBLISHABLE_KEY`) and someone is signed in; otherwise the stub (a second IndexedDB database), so
// keyless runs and CI keep working. Each remote keeps its own sync bases (`__sync`, `__sync:cloud:<user>`).
// supabase-js loads only when the keys are there. The autosave runs at zone changes (loading → play) and on quitting
// to the title. Settings are saved here too, as a named value synced with the "newer wins" policy.
// Since the composition framework's flow stream the mechanism is the save-slots feature (@rpg-unit/save-slots) and the
// triggers its checkpoint policy (@rpg-unit/save-policy-presets, whose 10 s gap replaced the tuning row
// `save.autosaveMinInterval`); this file passes the spike's schema, sim, backends and account in.
//
// Switches: `?sync=stub|off` (the fallback while signed out: default stub in dev builds, off in production; either
// one given explicitly also keeps the account's remote out); `?faults=no-save-migration` removes the sim slice's
// migration (the old-save fixture must then fail).
import { createIndexedDbBackend, createStubRemote, encodeSave, sliceData, type SaveDoc } from "@rpg/save";
import { randomId } from "@rpg/telemetry";
import type { SupabaseConnection } from "@rpg/save/supabase";
import { createSaveSlots, type SaveSlots } from "@rpg-unit/save-slots";
import { savePolicy } from "@rpg-unit/save-policy-presets";
import type { SimSave } from "../sim/host";
import { fromSimSlice, SAVE_FAULTS, SAVE_SCHEMA, toSimSlice, type SimSliceV3 } from "./schema";

export { SAVE_FAULTS } from "./schema";
export type { LoadOutcome } from "@rpg-unit/save-slots";

/** The fault names this module owns, for the spike's one `?faults=` switch. */
export const SAVE_FAULT_NAMES = ["no-save-migration", "phase-d-slices-kept"] as const;

/** What the saves need from the game. */
export interface SaveDeps {
  sim: { save(): SimSave; restore(s: SimSave): void; hash(): string };
  /** Sim steps per second (for the playtime shown on a slot). */
  stepHz: number;
  /** The flow's transitions, for the autosave triggers. */
  onFlow(fn: (e: { op: string; from: string | null; to: string | null }) => void): void;
  search?: string;
}

/** The spike's saves: @rpg-unit/save-slots over the cloud account's connection (the shared Supabase project's). */
export type SpikeSaves = SaveSlots<SupabaseConnection>;
/** Sign-in for the cloud saves. */
export type SpikeAccount = SpikeSaves["account"];

/** Turn on the planted defects `?faults=` names. */
export function applySaveFaults(search = location.search): void {
  const on = (new URLSearchParams(search).get("faults") ?? "").split(",");
  SAVE_FAULTS.noSaveMigration = on.includes("no-save-migration");
  SAVE_FAULTS.phaseDSlicesKept = on.includes("phase-d-slices-kept");
}

/** Open the store (and the sync), wire the autosave triggers: the save-slots feature with the spike's schema, its sim
 *  as the state, IndexedDB on this device, the stub or the account as the remote and the checkpoint policy. */
export function mountSaves(d: SaveDeps): SpikeSaves {
  const q = new URLSearchParams(d.search ?? location.search);
  applySaveFaults(d.search ?? location.search);
  const syncParam = q.get("sync");
  const fallbackMode = syncParam ?? (import.meta.env.DEV ? "stub" : "off");
  const env = import.meta.env as Record<string, string | undefined>;
  const cloudUrl = env.VITE_SUPABASE_URL;
  const cloudKey = env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const cloudConfigured = !!cloudUrl && !!cloudKey && syncParam !== "off" && syncParam !== "stub";
  const deviceId = (): string => {
    try {
      const k = "green-valley-device";
      return localStorage.getItem(k) ?? (localStorage.setItem(k, randomId()), localStorage.getItem(k)!);
    } catch {
      return "unknown";
    }
  };
  const snapshot = (): { slices: { sim: SimSliceV3 }; meta: Record<string, number | string> } => {
    const s = d.sim.save();
    return { slices: { sim: toSimSlice(s) }, meta: { step: s.step, playSeconds: Math.round(s.step / d.stepHz), hash: d.sim.hash() } };
  };
  const saves = createSaveSlots<SupabaseConnection>({
    schema: SAVE_SCHEMA,
    snapshot,
    apply: (doc: SaveDoc) => {
      d.sim.restore(fromSimSlice(sliceData(doc).sim as SimSliceV3));
      return d.sim.hash();
    },
    // spike 002's: an autosave at zone changes (loading → play) and on quitting to the title from pause, three slots
    policy: savePolicy("checkpoint"),
    local: createIndexedDbBackend({ db: "green-valley-saves" }),
    fallback: fallbackMode === "stub" ? createStubRemote(createIndexedDbBackend({ db: "green-valley-cloud-stub" })) : null,
    fallbackMode,
    cloud: {
      configured: cloudConfigured,
      // supabase-js loads only when the keys are there
      connect: () => import("@rpg/save/supabase").then((m) => m.connectSupabase({ url: cloudUrl!, key: cloudKey!, deviceId: deviceId(), storageKey: "green-valley-auth" })),
    },
    onFlow: d.onFlow,
  });
  // the spike's own handle key: the current state's save in every encoding (the numbers for the notes)
  saves.handle.measure = async () => {
    const s = d.sim.save();
    const doc = SAVE_SCHEMA.make({ sim: toSimSlice(s) });
    const v1 = SAVE_SCHEMA.make({ sim: toSimSlice(s) });
    (v1.slices.sim as { data: unknown }).data = s; // the v1 shape: base64 inside the JSON
    const out: Record<string, number> = { a1Json: JSON.stringify(s).length };
    for (const c of ["none", "gzip", "deflate-raw"] as const) {
      out[`bytes.${c}`] = (await encodeSave(doc, { compression: c })).stats.storedBytes;
      out[`base64.${c}`] = (await encodeSave(v1, { compression: c })).stats.storedBytes;
    }
    return out;
  };
  return saves;
}
